import React, { useEffect, useState, useRef } from 'react';
import axios from '../config/api';
import { readCurrentCoordinates } from '../utils/deviceLocation';
import { Link } from 'react-router-dom';
import { MapContainer, Marker, Popup, Polyline, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import { useToast } from '../context/ToastContext';
import AppTileLayer, { mapMotionOptions } from '../components/AppTileLayer';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const homeMarkerIcon = L.divIcon({
  className: 'custom-map-pin',
  html: `
    <div style="
      width:16px;height:16px;border-radius:9999px;background:#3b82f6;
      border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);
    "></div>
  `,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const workMarkerIcon = L.divIcon({
  className: 'custom-map-pin',
  html: `
    <div style="
      width:16px;height:16px;border-radius:9999px;background:#ef4444;
      border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);
    "></div>
  `,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const buildCafeDotIcon = (isOnRoute: boolean) =>
  L.divIcon({
    className: 'custom-map-pin',
    html: `
      <div style="
        width:${isOnRoute ? 14 : 12}px;height:${isOnRoute ? 14 : 12}px;border-radius:9999px;
        background:${isOnRoute ? '#22c55e' : '#f59e0b'};
        border:2px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,0.25);
      "></div>
    `,
    iconSize: isOnRoute ? [14, 14] : [12, 12],
    iconAnchor: isOnRoute ? [7, 7] : [6, 6],
  });

interface Cafe {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  subscription_active: boolean;
}

interface Location {
  address: string;
  lat: number;
  lng: number;
}

function MapUpdater({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

export default function RouteMap() {
  const { showToast } = useToast();
  const [cafes, setCafes] = useState<Cafe[]>([]);
  const [homeLocation, setHomeLocation] = useState<Location>({
    address: '',
    lat: 31.9539,
    lng: 35.9106
  });
  const [workLocation, setWorkLocation] = useState<Location>({
    address: '',
    lat: 31.9600,
    lng: 35.9300
  });
  const [cafesOnRoute, setCafesOnRoute] = useState<Cafe[]>([]);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([31.9539, 35.9106]);
  const [mapZoom, setMapZoom] = useState(13);

  useEffect(() => {
    fetchCafes();
  }, []);

  useEffect(() => {
    if (homeLocation.lat && workLocation.lat) {
      findCafesOnRoute();
      // Center map between home and work
      const centerLat = (homeLocation.lat + workLocation.lat) / 2;
      const centerLng = (homeLocation.lng + workLocation.lng) / 2;
      setMapCenter([centerLat, centerLng]);
    }
  }, [homeLocation, workLocation, cafes]);

  const fetchCafes = async () => {
    try {
      const response = await axios.get('/api/cafes');
      setCafes(response.data.filter((c: Cafe) => c.latitude && c.longitude));
    } catch (error) {
      console.error('Error fetching cafes:', error);
    }
  };

  const searchAddressSuggestions = async (query: string): Promise<any[]> => {
    if (!query.trim() || query.length < 2) return [];
    
    try {
      const response = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          q: `${query}, Amman, Jordan`,
          format: 'json',
          limit: 5,
          addressdetails: 1,
          countrycodes: 'jo'
        },
        headers: {
          'User-Agent': 'CoffeeApp/1.0'
        }
      });

      return response.data || [];
    } catch (error) {
      console.error('Search suggestions error:', error);
      return [];
    }
  };

  const geocodeAddress = async (address: string): Promise<Location | null> => {
    if (!address.trim()) return null;
    
    setLoading(true);
    try {
      // Using Nominatim (OpenStreetMap) geocoding service - free and no API key needed
      // Try multiple search strategies for better results
      const searchQueries = [
        `${address}, Amman, Jordan`,
        address, // Try without city first
        `${address}, Jordan`
      ];

      for (const query of searchQueries) {
        const response = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: {
            q: query,
            format: 'json',
            limit: 5, // Get more results to find the best match
            addressdetails: 1,
            countrycodes: 'jo' // Limit to Jordan
          },
          headers: {
            'User-Agent': 'CoffeeApp/1.0'
          }
        });

        if (response.data && response.data.length > 0) {
          // Prefer results in Amman, but accept any in Jordan
          const ammanResult = response.data.find((r: any) => 
            r.display_name.toLowerCase().includes('amman')
          ) || response.data[0];
          
          const result = ammanResult;
          return {
            address: result.display_name || address,
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon)
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      showToast('Could not find location. Please try a more specific address or place name.', 'warning');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const [homeAddressInput, setHomeAddressInput] = useState('');
  const [workAddressInput, setWorkAddressInput] = useState('');
  const [homeSuggestions, setHomeSuggestions] = useState<any[]>([]);
  const [workSuggestions, setWorkSuggestions] = useState<any[]>([]);
  const [showHomeSuggestions, setShowHomeSuggestions] = useState(false);
  const [showWorkSuggestions, setShowWorkSuggestions] = useState(false);

  // Debounce home address suggestions
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (homeAddressInput.trim() && homeAddressInput.length >= 2) {
        const suggestions = await searchAddressSuggestions(homeAddressInput);
        setHomeSuggestions(suggestions);
        setShowHomeSuggestions(true);
      } else {
        setHomeSuggestions([]);
        setShowHomeSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [homeAddressInput]);

  // Debounce work address suggestions
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (workAddressInput.trim() && workAddressInput.length >= 2) {
        const suggestions = await searchAddressSuggestions(workAddressInput);
        setWorkSuggestions(suggestions);
        setShowWorkSuggestions(true);
      } else {
        setWorkSuggestions([]);
        setShowWorkSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [workAddressInput]);

  const handleHomeAddressChange = (address: string) => {
    setHomeAddressInput(address);
    setHomeLocation({ ...homeLocation, address });
    if (address.trim()) {
      setShowHomeSuggestions(true);
    }
  };

  const handleWorkAddressChange = (address: string) => {
    setWorkAddressInput(address);
    setWorkLocation({ ...workLocation, address });
    if (address.trim()) {
      setShowWorkSuggestions(true);
    }
  };

  const handleSelectHomeSuggestion = async (suggestion: any) => {
    const address = suggestion.display_name;
    setHomeAddressInput(address);
    setShowHomeSuggestions(false);
    setHomeSuggestions([]);
    
    const location = {
      address: address,
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon)
    };
    setHomeLocation(location);
  };

  const handleSelectWorkSuggestion = async (suggestion: any) => {
    const address = suggestion.display_name;
    setWorkAddressInput(address);
    setShowWorkSuggestions(false);
    setWorkSuggestions([]);
    
    const location = {
      address: address,
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon)
    };
    setWorkLocation(location);
  };

  const handleHomeKeyPress = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setShowHomeSuggestions(false);
      if (homeAddressInput.trim()) {
        setLoading(true);
        const location = await geocodeAddress(homeAddressInput);
        if (location) {
          setHomeLocation(location);
          setHomeAddressInput(location.address);
        }
        setLoading(false);
      }
    }
  };

  const handleWorkKeyPress = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setShowWorkSuggestions(false);
      if (workAddressInput.trim()) {
        setLoading(true);
        const location = await geocodeAddress(workAddressInput);
        if (location) {
          setWorkLocation(location);
          setWorkAddressInput(location.address);
        }
        setLoading(false);
      }
    }
  };

  const getCurrentLocation = () => {
    setLoading(true);
    readCurrentCoordinates()
      .then(async ({ lat, lng }) => {
        try {
          const response = await axios.get('https://nominatim.openstreetmap.org/reverse', {
            params: {
              lat,
              lon: lng,
              format: 'json',
            },
            headers: {
              'User-Agent': 'CoffeeApp/1.0',
            },
          });

          const address = response.data?.display_name || 'Current Location';
          setHomeLocation({ address, lat, lng });
          setHomeAddressInput(address);
        } catch (error) {
          setHomeLocation({ address: 'Current Location', lat, lng });
          setHomeAddressInput('Current Location');
        } finally {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error getting location:', error);
        showToast(
          error?.message || 'Could not get your location. Please enter your address manually.',
          'warning'
        );
        setLoading(false);
      });
  };

  const findCafesOnRoute = () => {
    if (!homeLocation.lat || !workLocation.lat) return;

    const routeCafes = cafes.filter(cafe => {
      // Check if cafe is between home and work (not beyond the endpoints)
      const isBetween = isPointBetween(
        cafe.latitude, cafe.longitude,
        homeLocation.lat, homeLocation.lng,
        workLocation.lat, workLocation.lng
      );
      
      if (!isBetween) return false;
      
      // Also check distance from route line (within 1.5km)
      const distance = distanceFromLine(
        cafe.latitude, cafe.longitude,
        homeLocation.lat, homeLocation.lng,
        workLocation.lat, workLocation.lng
      );
      
      return distance < 1.5;
    });
    
    // Sort cafes by distance from home (closest to home first)
    routeCafes.sort((a, b) => {
      const distA = calculateDistance(
        homeLocation.lat, homeLocation.lng,
        a.latitude, a.longitude
      );
      const distB = calculateDistance(
        homeLocation.lat, homeLocation.lng,
        b.latitude, b.longitude
      );
      return distA - distB;
    });
    
    setCafesOnRoute(routeCafes);
  };

  // Check if a point is between two other points (not beyond the endpoints)
  const isPointBetween = (
    px: number, py: number,
    x1: number, y1: number,
    x2: number, y2: number
  ): boolean => {
    // Calculate vectors
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dpx = px - x1;
    const dpy = py - y1;
    
    // Dot product to check if point is in the direction of the line
    const dot = dpx * dx + dpy * dy;
    const lenSq = dx * dx + dy * dy;
    
    // Point is between if dot product is between 0 and lenSq
    return dot >= 0 && dot <= lenSq;
  };

  // Calculate distance between two points (in km)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const distanceFromLine = (px: number, py: number, x1: number, y1: number, x2: number, y2: number): number => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq !== 0) param = dot / lenSq;
    let xx, yy;
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy) * 111; // Convert to km
  };

  const routePolyline: [number, number][] = homeLocation.lat && workLocation.lat
    ? [[homeLocation.lat, homeLocation.lng], [workLocation.lat, workLocation.lng]]
    : [];

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-amber-800">Cafes on Your Route</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Set Your Route</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-sm font-semibold mb-2">Home Location</label>
            <div className="space-y-2">
              <div className="relative">
                <input
                  type="text"
                  value={homeAddressInput}
                  onChange={(e) => handleHomeAddressChange(e.target.value)}
                  onKeyPress={handleHomeKeyPress}
                  onFocus={() => homeSuggestions.length > 0 && setShowHomeSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowHomeSuggestions(false), 200)}
                  placeholder="e.g., Abdali Boulevard, City Mall, or Your Company Name"
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                {showHomeSuggestions && homeSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {homeSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectHomeSuggestion(suggestion)}
                        className="w-full text-left px-4 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-sm text-gray-800">
                          {suggestion.display_name.split(',')[0]}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {suggestion.display_name}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={getCurrentLocation}
                disabled={loading}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50 font-semibold"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)', pointerEvents: loading ? 'none' : 'auto' }}
              >
                {loading ? 'Locating...' : '📍 Use Current Location'}
              </button>
              {homeLocation.address && homeLocation.lat && (
                <p className="text-xs text-gray-500">
                  📍 {homeLocation.lat.toFixed(4)}, {homeLocation.lng.toFixed(4)}
                </p>
              )}
            </div>
          </div>
          <div className="relative">
            <label className="block text-sm font-semibold mb-2">Work Location</label>
            <div className="space-y-2">
              <div className="relative">
                <input
                  type="text"
                  value={workAddressInput}
                  onChange={(e) => handleWorkAddressChange(e.target.value)}
                  onKeyPress={handleWorkKeyPress}
                  onFocus={() => workSuggestions.length > 0 && setShowWorkSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowWorkSuggestions(false), 200)}
                  placeholder="e.g., Rainbow Street, King Hussein Business Park, or Your Office Name"
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                {showWorkSuggestions && workSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {workSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectWorkSuggestion(suggestion)}
                        className="w-full text-left px-4 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-sm text-gray-800">
                          {suggestion.display_name.split(',')[0]}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {suggestion.display_name}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {workLocation.address && workLocation.lat && (
                <p className="text-xs text-gray-500">
                  📍 {workLocation.lat.toFixed(4)}, {workLocation.lng.toFixed(4)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Map View */}
      {cafes.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Route Map</h2>
            <div className="flex items-center space-x-3">
              {homeLocation.lat && workLocation.lat && cafesOnRoute.length > 0 && (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                  {cafesOnRoute.length} cafe{cafesOnRoute.length > 1 ? 's' : ''} on route
                </span>
              )}
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                {cafes.length} total cafe{cafes.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs sm:text-sm text-gray-700">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-1.5"></div>
                <span>Home</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-1.5"></div>
                <span>Work</span>
              </div>
              <div className="flex items-center">
                <div className="w-5 h-1 bg-green-500 mr-1.5 rounded-full"></div>
                <span>Route</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-1.5 border border-white shadow-sm"></div>
                <span>On-route cafes</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-amber-500 rounded-full mr-1.5"></div>
                <span>Other cafes</span>
              </div>
            </div>
          </div>
          <div className="relative z-0 h-[26rem] sm:h-96 w-full border border-gray-200">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
              scrollWheelZoom
              {...mapMotionOptions}
            >
              <MapUpdater center={mapCenter} zoom={mapZoom} />
              <ZoomControl position="topright" />
              <AppTileLayer />
              
              {/* Route Line - Green line connecting home and work */}
              {homeLocation.lat && workLocation.lat && routePolyline.length > 0 && (
                <Polyline
                  positions={routePolyline}
                  color="#22c55e"
                  weight={4}
                  opacity={0.9}
                  lineCap="round"
                  lineJoin="round"
                />
              )}

              {/* Home Marker - only show if location is set */}
              {homeLocation.lat && homeLocation.lng && (
                <Marker position={[homeLocation.lat, homeLocation.lng]} icon={homeMarkerIcon}>
                  <Popup>
                    <strong>🏠 Home</strong><br />
                    {homeLocation.address}
                  </Popup>
                </Marker>
              )}

              {/* Work Marker - only show if location is set */}
              {workLocation.lat && workLocation.lng && (
                <Marker position={[workLocation.lat, workLocation.lng]} icon={workMarkerIcon}>
                  <Popup>
                    <strong>💼 Work</strong><br />
                    {workLocation.address}
                  </Popup>
                </Marker>
              )}

              {/* All Cafes - Highlighted if on route */}
              {cafes.map(cafe => {
                const isOnRoute = cafesOnRoute.some(c => c.id === cafe.id);
                return (
                  <Marker
                    key={cafe.id}
                    position={[cafe.latitude, cafe.longitude]}
                    icon={buildCafeDotIcon(isOnRoute)}
                  >
                    <Popup>
                      <div>
                        {isOnRoute && (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold mb-2 inline-block">
                            ✓ On Your Route
                          </span>
                        )}
                        <br />
                        <strong className="text-amber-800">☕ {cafe.name}</strong><br />
                        📍 {cafe.address}
                        {Boolean(cafe.subscription_active) && (
                          <span className="ml-2 bg-yellow-500 text-gray-800 px-2 py-1 rounded text-xs font-semibold">
                            Premium
                          </span>
                        )}
                        <br />
                        <Link
                          to={`/cafe/${cafe.id}`}
                          className="text-amber-700 hover:underline text-sm mt-1 inline-block"
                        >
                          View Menu →
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>
      )}

      <div>
        {homeLocation.lat && workLocation.lat && cafesOnRoute.length > 0 && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4 text-green-700">
              ✓ Cafes on Your Route ({cafesOnRoute.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cafesOnRoute.map(cafe => (
                <Link
                  key={cafe.id}
                  to={`/cafe/${cafe.id}`}
                  className="bg-green-50 border-2 border-green-300 rounded-lg shadow-md p-4 hover:shadow-lg transition"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-green-800">{cafe.name}</h3>
                    <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                      On Route
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">📍 {cafe.address}</p>
                  {Boolean(cafe.subscription_active) && (
                    <span className="bg-yellow-500 text-gray-800 px-2 py-1 rounded text-xs font-semibold">
                      Premium
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
        
        <div>
          <h2 className="text-2xl font-bold mb-4 text-amber-700">
            All Cafes ({cafes.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cafes.map(cafe => {
              const isOnRoute = cafesOnRoute.some(c => c.id === cafe.id);
              return (
                <Link
                  key={cafe.id}
                  to={`/cafe/${cafe.id}`}
                  className={`rounded-lg shadow-md p-4 hover:shadow-lg transition ${
                    isOnRoute 
                      ? 'bg-green-50 border-2 border-green-300' 
                      : 'bg-white border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-lg font-bold mb-2 ${
                      isOnRoute ? 'text-green-800' : 'text-amber-800'
                    }`}>
                      {cafe.name}
                    </h3>
                    {isOnRoute && (
                      <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                        On Route
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2">📍 {cafe.address}</p>
                  {Boolean(cafe.subscription_active) && (
                    <span className="bg-yellow-500 text-gray-800 px-2 py-1 rounded text-xs font-semibold">
                      Premium
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
